import { Dispatch } from "@reduxjs/toolkit";
import currency from "currency.js";
import { NextRouter } from "next/router";
import { PersistPartial } from "redux-persist/es/persistReducer";
import { confirmReceiptRequest } from "../services/hooks/hooksHelper/usePayHelper";
import { InitialState } from "../services/redux/slices/deliverySlice";
import { FranchiseStatusInitialState } from "../services/redux/slices/franchiseStatusSlice";
import {
  LoadingActionEnum,
  increasePendingOperations,
  pushToLoadingStack,
  setAddressSelectionDrawerState,
  setAnimatedGlobalBannerState,
  setLoading,
} from "../services/redux/slices/globalComponentsToggleSlice";
import {
  CartItemType,
  CartLoyaltyV2SliceType,
} from "../services/redux/types/CartLoyaltyV2Types";
import {
  TransformOfferType,
  TransformOfferTypeObject,
} from "../services/redux/types/TypeHelper";
import env from "../utils/env.config";
import CartItem from "./CartItem";
import LS from "./LocalStorage";
import Offer from "./OfferClass/Offer/Offer";
import {
  LocalStorageEnum,
  OnlinePaymentProvider,
  OrderTypesEnum,
  PAYMENT_METHOD,
  PagesEnum,
  URL_PARAMS,
} from "./enums";
import { addWithRoundingOrCurrency } from "./help/maths/maths";
import { getFranchiseSlug, getPageLink } from "./help/navigation/navigation";
import { getOrderTypeKeyStringByValue } from "./help/orderType/orderType";
import { validateTableForFranchise } from "./help/table/tableValidation";
import {
  Franchise,
  Order,
  OrderItem,
  Restaurant,
  cartNestedSubItemAddonsType,
  confirmReceiptBodyType,
  reduxCartAndLoyalty,
} from "./types";

const createReceiptReqBody = (
  orderType: OrderTypesEnum,
  cancel_url: string,
  isEligibleOffer: boolean,
  cartItems: CartItemType[],
  redeemedOffer?: Offer,
  receiptNote?: string
) => {
  try {
    if (!Array.isArray(cartItems)) {
      window.location.href = cancel_url;

      return;
    }

    if (cartItems.length === 0) {
      window.location.href = cancel_url;

      return;
    }

    const order = {} as Order;
    order.items = [];
    (cartItems as TransformOfferType<CartItem>[]).forEach((item) => {
      const orderItem = {} as OrderItem;
      orderItem.subItemId = item.subItemDetails.id!;
      orderItem.count = item.count;
      orderItem.note = item.note;
      orderItem.addons = [];
      orderItem.isFreeItem = item.isFreeItem;
      orderItem.isBundleItem = item.isBundleItem;
      orderItem.isBuyItem = item.isBuyItem;
      orderItem.isOfferItem = item.isOfferItem;
      orderItem.version = item.version;
      orderItem.price = currency(item.totalPrice).value;
      orderItem.excludeFromOffers = item.excludeFromOffers;
      orderItem.priceWithoutOffer = item.priceWithoutOffer;
      item.subItemDetails?.allAddonGroups?.forEach((addOn) => {
        addOn.addonGroupSubItem?.forEach((addonGroupSubItem) => {
          const nestedAddons: cartNestedSubItemAddonsType[] = [];
          addonGroupSubItem.subItem?.allAddonGroups?.forEach(
            (nestedAddonGroup) => {
              nestedAddonGroup.addonGroupSubItem.forEach((nestedAddon) => {
                nestedAddons.push({
                  id: nestedAddon.id,
                  count: nestedAddon.count || 1,
                });
              });
            }
          );
          orderItem.addons.push({
            id: addonGroupSubItem.id,
            count: addonGroupSubItem.count || 1,
            nestedAddons,
          });
        });
      });
      if (item.receiptItemId > -1) {
        orderItem.receiptItemId = item.receiptItemId;
      }
      orderItem.state = item.state;
      orderItem.discountedItemPercentage = item.discountedItemPercentage;

      if (checkIfOfferNotValid(redeemedOffer, isEligibleOffer, orderItem)) {
        return;
      }

      order.items.push(orderItem);
    });

    if (order.items.length === 0) {
      window.location.href = cancel_url;

      return;
    }

    order.orderType = orderType;

    if (receiptNote) {
      order.note = receiptNote;
    }

    return order;
  } catch (error) {
    window.location.href = cancel_url;
  }
};

type handleConfirmReceiptParams = {
  pageURL: string;
  paymentMethod: PAYMENT_METHOD;
  router: NextRouter;
  orderType: OrderTypesEnum;
  cartAndLoyalty: TransformOfferTypeObject<reduxCartAndLoyalty>;
  dispatch: Dispatch;
  deliveryAddress: InitialState & PersistPartial;
  delivery: InitialState & PersistPartial;
  hasRoundingMethodAndLevel: boolean;
  tableNumber: number;
  franchise: string;
  referenceName: string;
  restaurantInfo: Restaurant | null;
  onlinePaymentProvider?: OnlinePaymentProvider | undefined;
  tips?: number;
  savedCard?: any;
  isVisitor: boolean;
  redirectPage: PagesEnum;
  cartLoyaltyV2: CartLoyaltyV2SliceType;
  cartItems?: CartItemType[];
  cartTotal?: number;
  isEligibleOffer?: boolean;
  redeemedOfferId?: string | number;
  selfService?: boolean;
  scheduledPickup?: string;
  franchiseStatus?: FranchiseStatusInitialState;
};

export const handleConfirmReceipt = async ({
  pageURL,
  paymentMethod,
  router,
  orderType,
  cartAndLoyalty,
  dispatch,
  deliveryAddress,
  delivery,
  hasRoundingMethodAndLevel,
  tableNumber,
  franchise,
  referenceName,
  restaurantInfo,
  onlinePaymentProvider,
  tips,
  savedCard,
  isVisitor,
  redirectPage,
  cartLoyaltyV2,
  cartItems,
  cartTotal,
  isEligibleOffer,
  redeemedOfferId,
  selfService,
  scheduledPickup,
}: handleConfirmReceiptParams) => {
  if (!cartLoyaltyV2 || !cartLoyaltyV2.cart) {
    window.location.href = window.location.href.replace(
      pageURL,
      `/${PagesEnum.Cart}?${URL_PARAMS.EMPTY_CART}=true`
    );

    return;
  }

  const resolvedCartItems = cartItems ?? cartLoyaltyV2.cart.items;
  const resolvedCartTotal = cartTotal ?? cartLoyaltyV2.cart.totalPrice;
  const resolvedIsEligibleOffer =
    isEligibleOffer ?? cartLoyaltyV2.cart.isEligibleOffer;

  if (!Array.isArray(resolvedCartItems) || resolvedCartItems.length === 0) {
    window.location.href = window.location.href.replace(
      pageURL,
      `/${PagesEnum.Cart}?${URL_PARAMS.EMPTY_CART}=true`
    );

    return;
  }

  if (restaurantInfo?.config?.loadingStateSolution) {
    dispatch(
      pushToLoadingStack({
        action: LoadingActionEnum.INCREASE,
        functionName: "handleConfirmReceipt",
        date: new Date(),
      })
    );
    dispatch(increasePendingOperations());
  } else {
    dispatch(setLoading(true));
  }

  tips = tips || cartLoyaltyV2.cart.tips || 0;
  if (isVisitor && !LS._get(LocalStorageEnum.DEVICE_NUMBER)) {
    router.push(
      `${
        (getPageLink(router, PagesEnum.Login, {
          queryParams: [`${URL_PARAMS.PAY}=true`],
        }).href,
        getPageLink(router, PagesEnum.Login, {
          queryParams: [`${URL_PARAMS.PAY}=true`],
        }).as)
      }`
    );

    return;
  }
  let success_url = window.location.href.replace(pageURL, "/pay");
  success_url = success_url.replace(`&${URL_PARAMS.PAY}=true`, "");
  let cancel_url = window.location.href.replace(pageURL, "/cart");
  cancel_url = cancel_url.replace(
    `&${URL_PARAMS.PAY}=true`,
    `&${URL_PARAMS.EMPTY_CART}=true`
  );

  const redeemedOffer = redeemedOfferId
    ? cartAndLoyalty.offersArray.find(
        (o) => String(o.id) === String(redeemedOfferId)
      )
    : cartAndLoyalty.offersArray.find((o) =>
        o.isEqual(cartLoyaltyV2.cart.redeemedOffer)
      );

  const receiptNote = cartAndLoyalty.cart.receiptNote;

  const order = createReceiptReqBody(
    orderType,
    cancel_url,
    resolvedIsEligibleOffer,
    resolvedCartItems,
    redeemedOffer,
    receiptNote
  );

  // Add check for order creation failure
  if (!order) {
    window.location.href = cancel_url;

    return;
  }

  const cardSubtype = savedCard?.cardSubtype;
  const maskedPan = savedCard?.maskedPan;
  let redeemedOfferID: any;
  let promoCodeName: string;

  if (
    cartAndLoyalty.promoCode &&
    cartAndLoyalty.promoCode.valid &&
    cartAndLoyalty.promoCode.applied
  ) {
    promoCodeName = cartAndLoyalty.promoCode.name;
  }

  if (redeemedOfferId) {
    redeemedOfferID = redeemedOfferId;
  } else if (
    cartLoyaltyV2 &&
    cartLoyaltyV2.cart.isEligibleOffer &&
    cartLoyaltyV2.cart.redeemedOffer
  ) {
    redeemedOfferID = cartLoyaltyV2.cart.redeemedOffer.id;
  }

  // Fail closed when a map-based DELIVERYV2 order has no address, and also when
  // it has one but its zone fee never resolved.
  //
  // `deliveryFees` starts as null and is only filled once a zone matches the
  // address (setDeliveryMinMaxFees) or for flat, non-map configs
  // (setCurrentDeliveryConfig). The cart-calculate input coerces that null to 0,
  // so an unresolved fee is quoted to the customer as free delivery while the
  // backend charges the real zone fee on confirm — the customer is charged more
  // than the app showed them. Blocking here keeps the quote and the charge in
  // agreement instead.
  if (
    orderType === OrderTypesEnum.DELIVERYV2 &&
    delivery.currentDeliveryConfig?.usesMap &&
    (!deliveryAddress.selectedAddress?.id || delivery.deliveryFees == null)
  ) {
    dispatch(setAddressSelectionDrawerState(true));
    router.push(
      getPageLink(router, PagesEnum.Home).href,
      getPageLink(router, PagesEnum.Home).as
    );
    dispatch(
      setAnimatedGlobalBannerState({
        isOpen: true,
        text: "Please select an address",
        warning: true,
      })
    );

    return;
  }

  const totalBillPrice = resolvedCartTotal;

  if (totalBillPrice === 0) {
    paymentMethod = PAYMENT_METHOD.NONE;
  }

  const scheduledSlotId =
    cartAndLoyalty.selectedScheduledOptions?.selectedSlot?.id;
  const numberOfGuests =
    orderType === OrderTypesEnum.DINE_IN
      ? cartAndLoyalty.selectedScheduledOptions?.numberOfGuests
      : undefined;

  const orderTypeString = getOrderTypeKeyStringByValue(order.orderType);

  const deviceNumber = LS._get(LocalStorageEnum.DEVICE_NUMBER);

  const franchiseSlug = getFranchiseSlug(franchise);

  const selectedFranchise: Franchise | null =
    restaurantInfo?.franchises?.find((f) => f.slug === franchiseSlug) || null;

  let validatedTableNumber = tableNumber;
  if (selectedFranchise) {
    const isValidTable = validateTableForFranchise(
      tableNumber,
      selectedFranchise,
      franchiseSlug
    );

    if (!isValidTable) {
      validatedTableNumber = 1;
      console.warn(
        `Table ${tableNumber} does not exist for franchise ${franchiseSlug}. Using default table 1.`
      );
    }
  } else {
    // If franchise is not found, use default table to prevent backend errors
    validatedTableNumber = 1;
  }

  const requestBody: confirmReceiptBodyType = {
    ...(paymentMethod === PAYMENT_METHOD.ONLINE_CARD
      ? {
          paymentMethod,
          tips,
          success_url,
          cancel_url,
          cardSubtype,
          maskedPan,
          onlinePaymentProvider,
          // Tap web uses the hosted checkout: "src_all" makes Tap render every
          // method enabled on the MID (card / KNET / Benefit / Apple / Google
          // Pay) and detect device support. Ignored by the other providers.
          tapSource: "src_all",
          totalBillPrice: addWithRoundingOrCurrency(
            hasRoundingMethodAndLevel,
            totalBillPrice,
            tips
          ),
        }
      : {
          paymentMethod,
          totalBillPrice,
        }),
    items: order.items,
    orderType: orderTypeString,
    tableNumber: validatedTableNumber,
    restaurantId: restaurantInfo?.id || parseInt(env.restaurantId!),
    franchiseSlug: franchiseSlug,
    mainOfferId: redeemedOfferID && +redeemedOfferID!,
    receiptNote: delivery.deliveryType?.id === 1 ? undefined : order.note,
    customerAddressId: deliveryAddress.currentDeliveryConfig?.usesMap
      ? deliveryAddress.selectedAddress?.id
      : undefined,
    franchiseDeliveryConfigId: deliveryAddress.currentDeliveryConfig?.id,
    promoCode: promoCodeName!,
    scheduledSlotId,
    numberOfGuests,
    scheduledPickup,
    deviceNumber: deviceNumber ? Number(deviceNumber) : undefined,
  };

  await confirmReceiptRequest(
    requestBody,
    dispatch,
    referenceName,
    router,
    redirectPage,
    redeemedOffer,
    selfService,
    restaurantInfo?.config?.loadingStateSolution
  );
};

export const checkIfOfferNotValid = (
  redeemedOffer: Offer | undefined,
  isEligibleOffer: boolean,
  orderItem: OrderItem
): boolean => {
  return (
    (!redeemedOffer || !isEligibleOffer) &&
    (orderItem.isFreeItem ||
      orderItem.isOfferItem ||
      orderItem.discountedItemPercentage !== undefined)
  );
};
